import React from 'react';
import { buildTariffExample, tariffExampleLines } from './tariffExample';
import {
    Box,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

/**
 * One package's price list, a row per zone.
 *
 * Under the destination-only model a package is priced once per zone, so the
 * zone is the row and the package is the sheet: the mirror image of the zone
 * deletion warning, where the zone is fixed and the packages are the rows.
 *
 * When a customer discount is supplied, list and net prices are shown together
 * so the customer can see what the discount is worth rather than just a number
 * with no reference.
 */
const TariffTable = ({ sheet, dense = false }) => {
    const discountPct = Number(sheet?.discount?.package_disc || 0);
    const hasDiscount = discountPct > 0;

    const money = (value) => (value === null || value === undefined ? '-' : `₹${Number(value).toFixed(2)}`);

    const example = buildTariffExample(sheet);
    const exampleLines = tariffExampleLines(example, '₹');

    // "per 1 kg" reads awkwardly in a column heading; "per kg" is the same thing.
    const excessUnitLabel = sheet.product.type === 'Document' ? 'per 0.5 kg' : 'per kg';

    return (
        <Box>
            <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {sheet.product.name}
                </Typography>
                <Chip label={sheet.product.code} size="small" variant="outlined" />
                <Chip label={sheet.product.type} size="small" />
                <Chip
                    label={sheet.product.tax_calculation_type === 'inclusive' ? 'Tax inclusive' : 'Tax exclusive'}
                    size="small"
                    variant="outlined"
                />
                {hasDiscount && (
                    <Chip label={`${discountPct}% discount applied`} size="small" color="success" />
                )}
            </Stack>

            {/* The product description is deliberately not shown: it is free text
                that usually just restates the weight bands, which the table below
                already lists accurately. */}

            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size={dense ? 'small' : 'medium'}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Zone</TableCell>
                            <TableCell>Default Band</TableCell>
                            <TableCell align="right">Base Rate</TableCell>
                            {/* Documents are charged per 0.5 kg, so the unit is
                                taken from the product rather than hardcoded. */}
                            <TableCell>Weight Bands with Additional Rate {excessUnitLabel}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sheet.zones.map((z) => (
                            <TableRow key={z.zone_code} sx={z.not_priced ? { opacity: 0.55 } : undefined}>
                                {/* Name only. The code is an internal key and
                                    means nothing to a customer reading a tariff. */}
                                <TableCell>{z.zone}</TableCell>
                                <TableCell>{z.default_band}</TableCell>
                                <TableCell align="right">
                                    {z.base_rate === null ? (
                                        // An empty cell would read as "free"; say
                                        // plainly that this zone cannot be quoted.
                                        <Typography variant="caption" color="warning.main">
                                            Not priced
                                        </Typography>
                                    ) : hasDiscount ? (
                                        <>
                                            <Typography variant="body2" fontWeight={600}>
                                                {money(z.net_base_rate)}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ textDecoration: 'line-through' }}
                                            >
                                                {money(z.base_rate)}
                                            </Typography>
                                        </>
                                    ) : (
                                        money(z.base_rate)
                                    )}
                                </TableCell>
                                <TableCell>
                                    {/* The unit is stated once in the column
                                        heading, so it is not repeated per row. */}
                                    {z.bands?.length > 0
                                        ? z.bands.map((b, i) => (
                                            <Typography key={i} variant="caption" display="block">
                                                {b.range}: {money(hasDiscount ? b.net_rate : b.rate)}
                                            </Typography>
                                        ))
                                        : '-'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                The base rate covers any shipment up to the top of the default band. Beyond it,
                the weight-band rate is charged {sheet.product.excess_unit} of excess weight.
            </Typography>

            {/* A worked sum using this sheet's own rates. The rule above is
                abstract; the arithmetic is what people actually check against. */}
            {example && (
                <Box
                    sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        borderLeft: 3,
                        borderColor: 'primary.main',
                    }}
                >
                    {exampleLines.map((line, i) => (
                        <Typography
                            key={i}
                            variant="caption"
                            display="block"
                            sx={{
                                fontWeight: i === 0 || i === exampleLines.length - 1 ? 600 : 400,
                                mt: i === exampleLines.length - 1 && exampleLines.length > 1 ? 0.5 : 0,
                            }}
                        >
                            {line}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default TariffTable;
